import { useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { hasModuleAccess, hasWildcardAccess } from "@/lib/permissions";
import { ExternalLink, Lock, LayoutGrid } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { apps } from "@/config/apps";
import { AppConfig } from "@/types/app";
import { appendTokenToCmsRedirect } from "@/utils/redirectHelpers";
import { useAppSelector } from "@/store/hooks";
import { CATEGORY_LABELS as categoryLabels, CATEGORY_ORDER as categoryOrder } from "@/utils/constants";

interface AppListProps {
    permissions: string[];
}

export function AppList({ permissions }: AppListProps) {
    const { token, jwt } = useAppSelector((state) => state.auth);
    const authToken = token || jwt || null;

    const categorizedApps = useMemo(() => {
        const grouped = apps.filter(app => !app.hidden).reduce((acc, app) => {
            if (!acc[app.category]) {
                acc[app.category] = [];
            }
            acc[app.category].push(app);
            return acc;
        }, {} as Record<string, AppConfig[]>);

        return Object.entries(grouped)
            .map(([category, apps]) => ({
                category: category as AppConfig["category"],
                label: categoryLabels[category as AppConfig["category"]],
                apps,
            }))
            .sort((a, b) => {
                const aIndex = categoryOrder.indexOf(a.category);
                const bIndex = categoryOrder.indexOf(b.category);
                return aIndex - bIndex;
            });
    }, []);

    const getAppUrl = useCallback((app: AppConfig, hasAccess: boolean): string | null => {
        if (!hasAccess || app.link === "#" || !app.link) {
            return null;
        }
        const finalUrl = appendTokenToCmsRedirect(app.link, authToken);
        return (finalUrl && finalUrl !== "#") ? finalUrl : null;
    }, [authToken]);

    const handleAppClick = useCallback((app: AppConfig, hasAccess: boolean, event: React.MouseEvent) => {
        const url = getAppUrl(app, hasAccess);
        if (!url) {
            event.preventDefault();
            return;
        }
        
        window.open(url, "_blank");
    }, [getAppUrl]);

    return (
        <div className="animate-fade-in w-full max-w-[1600px] mx-auto pb-8 space-y-8">
            <div className="sticky top-16 z-10 backdrop-blur 
            -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 transition-all duration-300">
                <PageHeader
                    icon={LayoutGrid}
                    title="Apps"
                    description="Access your authorized applications"
                />
            </div>

            <div className="space-y-8">
                {categorizedApps.map(({ category, label, apps: categoryApps }) => (
                        <div key={category} id={category} className="scroll-mt-24">
                            <div className="flex items-center gap-2 my-4">
                                <h3 className="text-s font-medium uppercase text-muted-foreground/80">{label}</h3>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-10 sm:gap-4">
                                {categoryApps.map((app) => {
                                    // Determine lock display based on permissions (visual only — navigation is always allowed)
                                    let hasPermission = false;
                                    if (app.alwaysAccessible) {
                                        hasPermission = true;
                                    } else if (app.requiresSuperAdmin) {
                                        hasPermission = hasWildcardAccess(permissions);
                                    } else if (app.permissionModules) {
                                        hasPermission = app.permissionModules.some(module => hasModuleAccess(permissions, module));
                                    }
                                    const isLocked = !hasPermission;
                                    const appUrl = getAppUrl(app, hasPermission);

                                    return (
                                        <Card
                                            key={app.name}
                                            onClick={(e) => handleAppClick(app, hasPermission, e)}
                                            className={`
                                                relative p-6 group flex flex-col h-full transition-all duration-300
                                                border-2 rounded-xl overflow-hidden
                                                ${isLocked
                                                    ? "border-border/30 bg-muted/10 opacity-50 cursor-not-allowed"
                                                    : "border-border/50 bg-gradient-to-br from-card to-card/50 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-1 cursor-pointer"
                                                }
                                            `}
                                        >
                                            <div className="absolute top-4 right-4">
                                                {isLocked ? (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted/30 border border-border/50">
                                                                    <Lock className="w-3 h-3 text-muted-foreground" />
                                                                    <span className="text-[10px] font-medium text-muted-foreground">Locked</span>
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="left">
                                                                <p className="text-xs">Contact admin for access</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                ) : (
                                                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 opacity-0 group-hover:opacity-100 transition-all">
                                                        <ExternalLink className="w-3 h-3 text-primary" />
                                                        <span className="text-[10px] font-medium text-primary">Launch</span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex justify-center mb-5">
                                                <div className={`
                                                    relative w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center transition-all duration-300
                                                    ${isLocked
                                                        ? "bg-muted/50 grayscale"
                                                        : `bg-gradient-to-br ${app.color} shadow-lg shadow-primary/20 group-hover:scale-110 group-hover:rotate-3`
                                                    }
                                                `}>
                                                    <app.icon className="w-7 h-7" />
                                                    {!isLocked && (
                                                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex-1 flex flex-col items-center text-center min-w-0">
                                                <h3 className={`
                                                    font-bold text-lg mb-2 transition-colors w-full
                                                    ${isLocked ? "text-muted-foreground" : "text-foreground group-hover:text-primary"}
                                                `}>
                                                    {app.name}
                                                </h3>
                                                <p className="text-sm text-muted-foreground/70 line-clamp-2 leading-relaxed mb-4 flex-1">
                                                    {app.description}
                                                </p>
                                            </div>

                                            <div className="mt-auto pt-4 border-t border-border/40">
                                                <div className="flex justify-center">
                                                    <span className={`
                                                        text-[10px] font-mono px-2.5 py-1 rounded-md truncate max-w-full
                                                        ${isLocked
                                                            ? "bg-muted/30 text-muted-foreground/60"
                                                            : "bg-muted/40 text-muted-foreground/80 group-hover:bg-primary/10 group-hover:text-primary/90"
                                                        }
                                                        transition-colors
                                                    `}>
                                                        {app.label}
                                                    </span>
                                                </div>
                                            </div>

                                            {!isLocked && (
                                                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/5 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                                            )}
                                        </Card>
                                    );
                                })}
                            </div>
                        </div>
                ))}
            </div>
        </div>
    );
}
export { apps };

